<template>
  <div class="text-center">
    <v-menu v-model="menu" :close-on-content-click="false" bottom offset-y>
      <template v-slot:activator="{ props }">
        <v-chip :color="filteredStatus ? 'primary' : 'primary'" v-bind="props" label>
          {{ $t("common.action.status") }}
          <span v-if="filteredStatus">&nbsp;|&nbsp;</span>
          <span class="font-weight-bold"> {{ getStatusLabel }}</span>
          <v-btn
            v-if="filteredStatus"
            variant="text"
            size="x-small"
            icon="mdi-close"
            @click.stop="reset()"
          />
        </v-chip>
      </template>

      <v-card min-width="350" class="mt-2">
        <v-list>
          <v-list-item :title="$t('common.action.status')">
            <template v-slot:append>
              <v-btn variant="text" icon="mdi-close" @click="menu = false" />
            </template>
          </v-list-item>
        </v-list>

        <v-divider />

        <v-list class="mt-1 mx-6">
          <v-select
            class="pt-3"
            v-model="status"
            :clearable="!lockToActiveInactive"
            :items="getEffectiveConfig().statusList"
            :label="$t('common.action.status')"
          />
        </v-list>

        <v-card-actions>
          <v-spacer />
          <v-btn @click="applyAndCloseFilter" color="primary" class="mr-2">
            {{ $t("page.payment.filter.applyFilter") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue";
import i18n from "@/app/plugins/i18n";
import { usePaymentStore } from "@/payments/stores/payments";
import { useProductClassesStore } from "@/catalog/stores/productClasses";
import { useProductTagsStore } from "@/catalog/stores/productTags";
import type { PaymentFilterCondition } from "@/payments/types/payments";
import type { FilterCondition } from "@/shared/types/filters";

// A filter condition maps condition keys to raw store values.
type StatusFilterCondition = FilterCondition;

interface StatusTranslator {
  toStore: (value: string) => string | boolean | undefined;
  fromStore: (value: unknown) => string;
  getLabel: (value: string) => string;
}

interface StatusFilterStoreModule {
  setFilterProductClassCondition?: (condition: StatusFilterCondition) => void;
  setFilterProductTagCondition?: (condition: StatusFilterCondition) => void;
  filterProductClassCondition?: StatusFilterCondition;
  filterProductTagCondition?: StatusFilterCondition;
}

interface StatusFilterConfig {
  statusList: Array<{ title: string; value: string }>;
  conditionKey: string;
  translator?: StatusTranslator;
  storeModule?: StatusFilterStoreModule;
  searchMethod?: (opts?: object, reload?: boolean) => Promise<unknown>;
}

interface StatusFilterContextConfig extends Omit<StatusFilterConfig, "conditionKey"> {
  conditionKey?: string;
}

// Props
interface Props {
  lockToActiveInactive?: boolean;
  // Status list items to display in the dropdown
  statusList?: Array<{ title: string; value: string }>;
  // Key in the filter condition object that holds the status value
  conditionKey?: string;
  // Custom translator for converting between UI values and store values
  translator?: StatusTranslator;
  // Store module that handles the filter condition
  storeModule?: StatusFilterStoreModule;
  // Method to search with updated filters
  searchMethod?: (opts?: unknown, reload?: boolean) => Promise<unknown>;
  // Getter for the filter condition
  filterConditionGetter?: { value: StatusFilterCondition };
  // Setter for the filter condition
  filterConditionSetter?: (condition: StatusFilterCondition) => void;
  // Context to determine which store to use
  context?: string;
}

const props = withDefaults(defineProps<Props>(), {
  lockToActiveInactive: true,
  conditionKey: "status",
  translator: undefined,
  storeModule: undefined,
  searchMethod: undefined,
  filterConditionGetter: undefined,
  filterConditionSetter: undefined,
  context: "",
  statusList: () => [],
});

const menu = ref(false);
const status = ref("");
const contextConfig = ref<StatusFilterContextConfig | null>(null);

// Setup context-specific configuration
onMounted(() => {
  if (props.context === "product-class") {
    setupProductClassContext();
  } else if (props.context === "product-tag") {
    setupProductTagContext();
  }
});

// Product class context configuration
const setupProductClassContext = () => {
  const productClassesStore = useProductClassesStore();

  // Initialize filter condition if needed
  if (!productClassesStore.filterProductClassCondition) {
    productClassesStore.filterProductClassCondition = {};
  }

  contextConfig.value = {
    storeModule: productClassesStore,
    conditionKey: "active",
    statusList: [
      { title: i18n.global.t("common.action.active"), value: "true" },
      { title: i18n.global.t("common.action.inactive"), value: "false" },
    ],
    translator: {
      toStore: (value: string) => {
        if (value === "true") return true;
        if (value === "false") return false;
        return undefined;
      },
      fromStore: (value) => {
        if (value === true) return "true";
        if (value === false) return "false";
        return "";
      },
      getLabel: (value: string) => {
        if (value === "true") return i18n.global.t("common.action.active");
        if (value === "false") return i18n.global.t("common.action.inactive");
        return "";
      },
    },
    searchMethod: (opts = {}, reload = true) => {
      return productClassesStore.searchProductClasses(opts, reload);
    },
  };
};

// Product tag context configuration
const setupProductTagContext = () => {
  const productTagsStore = useProductTagsStore();

  // Initialize filter condition if needed
  if (!productTagsStore.filterProductTagCondition) {
    productTagsStore.filterProductTagCondition = {};
  }

  contextConfig.value = {
    storeModule: productTagsStore,
    conditionKey: "active",
    statusList: [
      { title: i18n.global.t("common.action.active"), value: "true" },
      { title: i18n.global.t("common.action.inactive"), value: "false" },
    ],
    translator: {
      toStore: (value: string) => {
        if (value === "true") return true;
        if (value === "false") return false;
        return undefined;
      },
      fromStore: (value) => {
        if (value === true) return "true";
        if (value === false) return "false";
        return "";
      },
      getLabel: (value: string) => {
        if (value === "true") return i18n.global.t("common.action.active");
        if (value === "false") return i18n.global.t("common.action.inactive");
        return "";
      },
    },
    searchMethod: (opts = {}, reload = true) => {
      return productTagsStore.searchProductTags(opts, reload);
    },
  };
};

// Get the effective configuration based on context or props
const getEffectiveConfig = (): StatusFilterConfig => {
  if (props.context && contextConfig.value) {
    return {
      statusList:
        contextConfig.value.statusList.length > 0
          ? contextConfig.value.statusList
          : props.statusList,
      conditionKey: contextConfig.value.conditionKey || props.conditionKey,
      translator: contextConfig.value.translator || props.translator,
      storeModule: contextConfig.value.storeModule || props.storeModule,
      searchMethod: contextConfig.value.searchMethod || props.searchMethod,
    };
  }

  return {
    statusList: props.statusList,
    conditionKey: props.conditionKey,
    translator: props.translator,
    storeModule: props.storeModule,
    searchMethod: props.searchMethod,
  };
};

// If we're using the payment store as default (for backward compatibility)
const usePaymentStoreDefault = computed(() => {
  const config = getEffectiveConfig();
  return !config.storeModule && !props.filterConditionSetter;
});

// Use either the provided store module or fall back to the payment store
const getPaymentStoreIfDefault = () => {
  if (usePaymentStoreDefault.value) {
    return usePaymentStore();
  }
  return undefined;
};

// For backward compatibility with payment store
const defaultSetFilterCondition = (condition: StatusFilterCondition) => {
  const config = getEffectiveConfig();
  const paymentStore = getPaymentStoreIfDefault();
  if (paymentStore) {
    paymentStore.setFilterPaymentCondition(condition as PaymentFilterCondition);
  } else if (props.filterConditionSetter) {
    props.filterConditionSetter(condition);
  } else if (config.storeModule) {
    if (props.context === "product-class") {
      config.storeModule.setFilterProductClassCondition?.(condition);
    } else if (props.context === "product-tag") {
      config.storeModule.setFilterProductTagCondition?.(condition);
    }
  }
};

// Get the current filter condition
const getFilterCondition = (): StatusFilterCondition => {
  const config = getEffectiveConfig();
  if (props.filterConditionGetter) {
    return props.filterConditionGetter.value;
  }
  const paymentStore = getPaymentStoreIfDefault();
  if (paymentStore) {
    return paymentStore.filterPaymentCondition as StatusFilterCondition;
  }
  if (props.context === "product-class") {
    return config.storeModule?.filterProductClassCondition || {};
  }
  if (props.context === "product-tag") {
    return config.storeModule?.filterProductTagCondition || {};
  }
  return {};
};

// Compute the filtered status
const filteredStatus = computed(() => {
  const condition = getFilterCondition();
  const config = getEffectiveConfig();
  const key = config.conditionKey;

  if (condition && key in condition) {
    if (config.translator) {
      return config.translator.fromStore(condition[key]);
    }
    return (condition[key] as string) || "";
  }

  return "";
});

// Get the display label for the current status
const getStatusLabel = computed(() => {
  const config = getEffectiveConfig();
  if (config.translator && filteredStatus.value) {
    return config.translator.getLabel(filteredStatus.value);
  }
  return filteredStatus.value;
});

watch(
  filteredStatus,
  (value) => {
    if (!menu.value) {
      status.value = value;
    }
  },
  { immediate: true },
);

// Reset the filter
const reset = async () => {
  const condition = getFilterCondition();
  const config = getEffectiveConfig();

  if (
    props.lockToActiveInactive &&
    (props.context === "product-class" ||
      props.context === "product-tag" ||
      config.conditionKey === "active")
  ) {
    status.value = "true";
    condition[config.conditionKey] = true;
  } else {
    status.value = "";
    delete condition[config.conditionKey];
  }

  defaultSetFilterCondition(condition);

  if (config.searchMethod) {
    const tableOnlyLoading = props.context === "product-class" || props.context === "product-tag";
    await config.searchMethod({}, !tableOnlyLoading);
  } else if (usePaymentStoreDefault.value) {
    await getPaymentStoreIfDefault()?.getFilteredPayments();
  }
};

// Apply the filter and close the menu
const applyAndCloseFilter = () => {
  updateFilter();
  menu.value = false;
};

// Update the filter with the selected status
const updateFilter = () => {
  const oldCondition = getFilterCondition();
  const newCondition = { ...oldCondition };
  const config = getEffectiveConfig();

  if (config.translator) {
    const storeValue = config.translator.toStore(status.value || "");
    if (storeValue !== undefined) {
      newCondition[config.conditionKey] = storeValue;
    } else {
      delete newCondition[config.conditionKey];
    }
  } else {
    // Default behavior for payment status
    const newStatus = (status.value || "").toLowerCase().replace(/\s+/g, "_");
    if (newStatus) {
      newCondition[config.conditionKey] = newStatus;
    } else {
      delete newCondition[config.conditionKey];
    }
  }

  defaultSetFilterCondition(newCondition);

  if (config.searchMethod) {
    const tableOnlyLoading = props.context === "product-class" || props.context === "product-tag";
    config.searchMethod({}, !tableOnlyLoading);
  } else if (usePaymentStoreDefault.value) {
    getPaymentStoreIfDefault()?.getFilteredPayments();
  }

  status.value = filteredStatus.value;
};
</script>
